import { defineStore } from 'pinia';
import { ref } from 'vue';
import { updateSentryTags } from '@/libraries/sentry';

import { useAppStore } from '@/stores/pinia/useAppStore';

type QueueType = 'pageViews' | 'events' | 'consents';
type TrackingQueues = Record<QueueType, any[]>;
export type SessionEnvironmentOrigin =
	| 'seo'
	| 'organic'
	| 'social'
	| 'search'
	| 'direct'
	| 'redirect'
	| 'returning'
	| 'jwtv';

export type PageEntryMode = 'modal';

export type SessionEnvironment = {
	sessionReady: boolean;
	origin: SessionEnvironmentOrigin | null;
	hasAdBlocker: boolean | null;
	originPageUrl: string | null;
	originPageReferred: string | null;
	originRefrMedium: string | null;
	originRefrSource: string | null;
	originRefrSocial: string | null;
	env: string | null;
};

export type OverridePageState = {
	pageType: string;
	pageViewUUID: string;
	pageEntryMode?: 'modal';
} | null;
export const useTrackingStore = defineStore('tracking', () => {
	const appStore = useAppStore();

	// SSR SR Campaigns rely on pagesVisted for first page view detection
	const pagesVisited = ref(0);
	const queues = ref<TrackingQueues>({
		pageViews: [],
		events: [],
		consents: [],
	});

	const sessionEnvironment = ref<SessionEnvironment>({
		sessionReady: false,
		origin: null,
		env: null,
		hasAdBlocker: null,
		originPageUrl: null,
		originPageReferred: null,
		originRefrMedium: null,
		originRefrSource: null,
		originRefrSocial: null,
	});

	const pageViewUUID = ref<string | null>(null);
	const overridePageState = ref<OverridePageState>(null);

	function getQueueOf(queueType: QueueType) {
		return queues.value[queueType];
	}

	function setQueues(trackingQueues: TrackingQueues) {
		queues.value = trackingQueues;
	}

	function resetQueue(queueType: QueueType) {
		queues.value[queueType] = [];
	}

	function addToQueue({ queueType, event }: { queueType: QueueType; event: any }) {
		queues.value[queueType].push(event);
	}

	function increasePagesVisited() {
		if (import.meta.server) return;

		pagesVisited.value++;

		// Update Sentry tag when navigating to second page
		if (pagesVisited.value === 2) {
			updateSentryTags({ first_page: false });
		}
	}

	function addPageView(event: any) {
		addToQueue({ queueType: 'pageViews', event });
	}

	function addTrackEvent(event: any) {
		addToQueue({ queueType: 'events', event });
	}

	function setPageViewUUID(UUID: string | null) {
		pageViewUUID.value = UUID;
	}

	function setSessionEnvironment(environment: Partial<SessionEnvironment>) {
		sessionEnvironment.value = { ...sessionEnvironment.value, ...environment };
	}

	function setSessionEnvironmentByKey<S extends keyof SessionEnvironment, V extends SessionEnvironment[S]>({
		key,
		value,
	}: {
		key: S;
		value: V;
	}) {
		sessionEnvironment.value[key] = value;
	}

	function setOverridePageState(pageState: OverridePageState) {
		overridePageState.value = pageState;
	}

	function setSessionEnvironmentContext({
		isArbitrating,
		utmMedium,
		utmSource,
	}: {
		isArbitrating: boolean;
		utmMedium?: string;
		utmSource?: string;
	}) {
		if (import.meta.server) return;

		const referrer = document ? document.referrer : null;
		const url = window ? window.location.href : null;

		const params = getQueryParams(url);
		const origin = getOrigin(referrer, isArbitrating, appStore.isSeoUser);

		setSessionEnvironment({
			origin,
			originPageUrl: url,
			originPageReferred: referrer,
			originRefrMedium: utmMedium || params.medium,
			originRefrSource: utmSource || params.source,
			originRefrSocial: origin === 'social' ? getSocialPlatform(referrer) : null,
			env: appStore.xServeTrack,
		});
	}

	return {
		pagesVisited,
		queues,
		sessionEnvironment,
		pageViewUUID,
		overridePageState,

		getQueueOf,
		setQueues,
		resetQueue,
		addToQueue,
		increasePagesVisited,
		addPageView,
		addTrackEvent,
		setPageViewUUID,
		setSessionEnvironment,
		setSessionEnvironmentByKey,
		setOverridePageState,
		setSessionEnvironmentContext,
	};
});

function getQueryParams(url: string | null) {
	if (!url) {
		return { medium: null, source: null };
	}
	const params = new URL(url).searchParams;
	return {
		medium: params.get('utm_medium') || null,
		source: params.get('utm_source') || null,
	};
}

function getOrigin(referrer: string | null, isArbitrating: boolean, isSeoUser: boolean) {
	if (isArbitrating) {
		return 'search';
	} else if (isSeoUser) {
		return 'seo';
	} else if (referrer?.includes('justwatch.tv')) {
		return 'jwtv';
	} else if (isSocial(referrer)) {
		return 'social';
	} else if (!referrer) {
		return 'direct';
	}

	// no paid search, seoUser false, no social, has refferer - assume returning
	return 'returning';
}

const SOCIAL_RE =
	/(?:https?:\/\/)?(?:\w+\.)?(t\.co|facebook\.com|x\.com|twitter\.com|instagram\.com|youtube\.com|tiktok\.com|snapchat\.com|reddit\.com|bsky\.app|linkedin\.com)/i;

function isSocial(referrer: string | null) {
	if (!referrer) return false;

	return SOCIAL_RE.test(referrer);
}

function getSocialPlatform(referrer: string | null): string | null {
	if (!referrer) return null;

	const match = referrer.match(SOCIAL_RE);
	return match?.[1] ? match[1].replace(/\.com$/, '') : null;
}
